/* eslint-disable jsx-a11y/no-autofocus */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  FormLabel,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  List,
  ListItem,
  Spinner,
  Tag,
  TagLabel,
  Text,
  Textarea,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { MagnifyingGlass, Sparkle, Star, Trash } from 'phosphor-react';
import { useFormContext } from 'react-hook-form';

import { ICnae, ICnaeItem, useSocietario } from '../../../../../hooks/useSocietario';
import { extractApiErrorMessage } from '../../../../../utils/ErrorUtils';
import { IWizardFormValues } from '../schema';

const DEBOUNCE_MS = 300;

const CnaeRow = ({
  cnae,
  isPrincipal,
  onTogglePrincipal,
  onRemove,
}: {
  cnae: ICnaeItem;
  isPrincipal: boolean;
  onTogglePrincipal: () => void;
  onRemove: () => void;
}) => (
  <Flex
    align="center"
    gap={2}
    p={2}
    border="1px solid"
    borderColor={isPrincipal ? 'orange.300' : 'gray.200'}
    bg={isPrincipal ? 'orange.50' : 'white'}
    borderRadius="md"
  >
    <IconButton
      aria-label={isPrincipal ? 'CNAE principal' : 'Definir como principal'}
      icon={<Star size={16} weight={isPrincipal ? 'fill' : 'regular'} />}
      size="xs"
      variant="ghost"
      colorScheme={isPrincipal ? 'orange' : 'gray'}
      onClick={onTogglePrincipal}
      title={isPrincipal ? 'Principal' : 'Definir como principal'}
    />
    <Box flex="1">
      <Text fontWeight="bold" fontSize="sm">
        {cnae.codigo}
        {isPrincipal && (
          <Badge ml={2} colorScheme="orange" fontSize="2xs">
            Principal
          </Badge>
        )}
      </Text>
      <Text fontSize="xs" color="gray.700" lineHeight="1.3">
        {cnae.descricao}
      </Text>
    </Box>
    <IconButton
      aria-label="Remover CNAE"
      icon={<Trash size={14} />}
      size="xs"
      variant="ghost"
      colorScheme="red"
      onClick={onRemove}
    />
  </Flex>
);

// `textoObjeto`: na alteração do objeto, a sugestão parte do texto que vai pro contrato.
export const CnaeSelector = ({ textoObjeto }: { textoObjeto?: string } = {}) => {
  const toast = useToast();
  const { buscarCnaes, sugerirCnaes } = useSocietario();
  const { watch, setValue } = useFormContext<IWizardFormValues>();
  const cnaesAtual = watch('cnaes') ?? [];

  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<ICnae[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  // Debounce real + race guard: setTimeout sem clearTimeout dispara N
  // requests por digitação rápida; e como a ordem de chegada não é
  // determinística, o ultimo setSearchResults pode ser de uma query antiga.
  // searchSeqRef garante que so a resposta da query mais recente prevalece.
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchSeqRef = useRef(0);
  useEffect(
    () => () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    },
    [],
  );

  const [descricaoIaLocal, setDescricaoIa] = useState('');
  const descricaoIa = textoObjeto ?? descricaoIaLocal;
  const [iaLoading, setIaLoading] = useState(false);
  const [iaRaciocinio, setIaRaciocinio] = useState<string>('');

  const codigosJaSelecionados = useMemo(
    () => new Set(cnaesAtual.map(c => c.codigo).filter(Boolean)),
    [cnaesAtual],
  );

  const algumPrincipal = cnaesAtual.some(c => c.principal);

  const aplicarLista = (lista: ICnaeItem[]) => {
    setValue('cnaes', lista, { shouldDirty: true, shouldValidate: true });
  };

  const handleSearchChange = useCallback(
    (q: string) => {
      setSearchTerm(q);
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      if (q.trim().length < 2) {
        setSearchResults([]);
        setSearchOpen(false);
        setSearchLoading(false);
        return;
      }
      setSearchLoading(true);
      setSearchOpen(true);
      searchSeqRef.current += 1;
      const seq = searchSeqRef.current;
      searchTimerRef.current = setTimeout(async () => {
        try {
          const items = await buscarCnaes(q.trim(), 15);
          // Race guard: descarta resposta se uma busca mais recente ja foi disparada.
          if (seq !== searchSeqRef.current) return;
          setSearchResults(items);
        } catch (err) {
          if (seq !== searchSeqRef.current) return;
          toast({
            status: 'error',
            description: extractApiErrorMessage(err, 'Falha ao buscar CNAE.'),
          });
        } finally {
          if (seq === searchSeqRef.current) setSearchLoading(false);
        }
      }, DEBOUNCE_MS);
    },
    [buscarCnaes, toast],
  );

  const adicionarManual = (cnae: ICnae) => {
    if (codigosJaSelecionados.has(cnae.codigo)) return;
    const tornarPrincipal = cnaesAtual.length === 0;
    aplicarLista([
      ...cnaesAtual.map(c => ({ ...c, principal: tornarPrincipal ? false : !!c.principal })),
      { codigo: cnae.codigo, descricao: cnae.descricao, principal: tornarPrincipal },
    ]);
  };

  const handleSugerir = async () => {
    if (descricaoIa.trim().length < 10) {
      toast({
        status: 'warning',
        description: 'Descreva a atividade com pelo menos 10 caracteres.',
      });
      return;
    }
    setIaLoading(true);
    try {
      const sug = await sugerirCnaes(descricaoIa.trim());
      const principalCodigo = sug.principal.codigo;
      const codigosNovos = new Set<string>([
        principalCodigo,
        ...sug.secundarios.map(s => s.codigo),
      ]);

      // Mantem os ja selecionados que NAO foram retornados pela IA, todos como secundarios.
      const existentesPreservados = cnaesAtual
        .filter(c => c.codigo && !codigosNovos.has(c.codigo))
        .map(c => ({ ...c, principal: false }));

      // 1 unico principal (o sugerido pela IA), demais como secundarios.
      const novosItens: ICnaeItem[] = [
        { codigo: sug.principal.codigo, descricao: sug.principal.descricao, principal: true },
        ...sug.secundarios
          .filter(s => s.codigo !== principalCodigo)
          .map(s => ({ codigo: s.codigo, descricao: s.descricao, principal: false })),
      ];

      aplicarLista([...existentesPreservados, ...novosItens]);
      setIaRaciocinio(sug.raciocinio);
      toast({
        status: 'success',
        description: `Sugeridos: principal ${sug.principal.codigo} + ${sug.secundarios.length} secundário(s).`,
      });
    } catch (err) {
      toast({
        status: 'error',
        description: extractApiErrorMessage(err, 'Falha ao sugerir CNAE.'),
      });
    } finally {
      setIaLoading(false);
    }
  };

  const setPrincipal = (indexAlvo: number) => {
    aplicarLista(cnaesAtual.map((c, idx) => ({ ...c, principal: idx === indexAlvo })));
  };

  const removerEm = (indexAlvo: number) => {
    aplicarLista(cnaesAtual.filter((_, idx) => idx !== indexAlvo));
  };

  return (
    <Box>
      <FormLabel fontWeight="bold" mb={1}>
        CNAEs
      </FormLabel>
      <Text fontSize="xs" color="gray.500" mb={3}>
        Busque pelo código ou descrição, ou descreva a atividade e use a sugestão por IA. Marque uma
        CNAE como principal (estrela laranja) — as demais vão como secundárias no contrato.
      </Text>

      {/* Sugestao por IA */}
      <Box
        border="1px dashed"
        borderColor="purple.300"
        bg="purple.50"
        p={3}
        borderRadius="md"
        mb={3}
      >
        {textoObjeto === undefined ? (
          <>
            <FormLabel fontSize="xs" mb={1} color="purple.700">
              Descrição da atividade (para sugerir CNAEs com IA)
            </FormLabel>
            <Textarea
              size="sm"
              rows={2}
              placeholder="Ex: Clínica de odontologia geral com especialização em implantes."
              value={descricaoIa}
              onChange={e => setDescricaoIa(e.target.value)}
              bg="white"
            />
          </>
        ) : (
          <Text fontSize="xs" color="purple.700">
            A sugestão usa o texto do novo objeto social, acima.
          </Text>
        )}
        <Flex mt={2} gap={2} align="center">
          <Button
            size="sm"
            colorScheme="purple"
            leftIcon={iaLoading ? <Spinner size="xs" /> : <Sparkle size={14} />}
            isLoading={iaLoading}
            loadingText="Consultando IA…"
            onClick={handleSugerir}
            isDisabled={iaLoading || descricaoIa.trim().length < 10}
          >
            Sugerir CNAEs com IA
          </Button>
          {iaRaciocinio && (
            <Text fontSize="xs" color="gray.600" flex="1">
              <strong>IA: </strong>
              {iaRaciocinio}
            </Text>
          )}
        </Flex>
      </Box>

      {/* Autocomplete manual */}
      <Box mb={3} position="relative">
        <InputGroup size="sm">
          <InputLeftElement pointerEvents="none">
            <MagnifyingGlass size={14} />
          </InputLeftElement>
          <Input
            placeholder="Buscar CNAE por descrição (ex: odonto, software, transporte)"
            value={searchTerm}
            onChange={e => handleSearchChange(e.target.value)}
            onFocus={() => searchResults.length > 0 && setSearchOpen(true)}
            onBlur={() => window.setTimeout(() => setSearchOpen(false), 200)}
          />
        </InputGroup>
        {searchOpen && (searchLoading || searchResults.length > 0) && (
          <Box
            position="absolute"
            top="100%"
            left={0}
            right={0}
            mt={1}
            bg="white"
            border="1px solid"
            borderColor="gray.200"
            borderRadius="md"
            boxShadow="md"
            maxH="280px"
            overflowY="auto"
            zIndex={10}
          >
            {searchLoading ? (
              <Flex p={3} align="center" gap={2}>
                <Spinner size="xs" /> <Text fontSize="sm">Buscando…</Text>
              </Flex>
            ) : (
              <List spacing={0}>
                {searchResults.map(cnae => {
                  const ja = codigosJaSelecionados.has(cnae.codigo);
                  return (
                    <ListItem
                      key={cnae.codigo}
                      p={2}
                      cursor={ja ? 'not-allowed' : 'pointer'}
                      opacity={ja ? 0.4 : 1}
                      _hover={{ bg: ja ? 'transparent' : 'gray.50' }}
                      onMouseDown={e => {
                        e.preventDefault();
                        if (!ja) {
                          adicionarManual(cnae);
                          setSearchTerm('');
                          setSearchResults([]);
                          setSearchOpen(false);
                        }
                      }}
                    >
                      <Text fontSize="xs" fontWeight="bold">
                        {cnae.codigo}
                      </Text>
                      <Text fontSize="xs" color="gray.700">
                        {cnae.descricao}
                      </Text>
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Box>
        )}
      </Box>

      {/* Lista de selecionados */}
      {cnaesAtual.length === 0 ? (
        <Box textAlign="center" py={3} color="gray.500" fontSize="sm">
          Nenhum CNAE selecionado.
        </Box>
      ) : (
        <VStack align="stretch" spacing={2}>
          {cnaesAtual.map((item, index) => {
            if (!item?.codigo) return null;
            return (
              <CnaeRow
                key={item.codigo || `placeholder-${item.descricao}`}
                cnae={item}
                isPrincipal={!!item.principal}
                onTogglePrincipal={() => setPrincipal(index)}
                onRemove={() => removerEm(index)}
              />
            );
          })}
        </VStack>
      )}

      {cnaesAtual.length > 0 && !algumPrincipal && (
        <HStack mt={2} color="orange.600" fontSize="xs">
          <Tag colorScheme="orange" size="sm">
            <TagLabel>Marque uma CNAE como principal</TagLabel>
          </Tag>
        </HStack>
      )}
    </Box>
  );
};

export default CnaeSelector;
